import dayjs from 'dayjs';
import { BatchExecution } from '../models/batch-execution.model';
import { buildXAxisDataZoom, CHART_GRID_BOTTOM, shouldShowSeriesLabels } from 'src/app/shared/components/charts/chart-zoom.util';

const DURATION_COLOR = '#2953A6';
const SERIES_COLOR = '#E8A87C';

const ptBrIntegerFormatter = new Intl.NumberFormat('pt-BR', {
  maximumFractionDigits: 0,
});

export function formatPtBrInteger(value: number): string {
  return ptBrIntegerFormatter.format(Math.round(value));
}

export function formatDurationHms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function formatDurationHm(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function formatStartedAtLabel(isoDate: string): string {
  return dayjs(isoDate).locale('pt-br').format('DD/MM/YYYY HH:mm');
}

function getNiceMax(value: number, step: number): number {
  if (value <= 0) return step;
  return Math.ceil(value / step) * step;
}

export function buildBatchExecutionsChartOptions(rows: BatchExecution[]): Record<string, unknown> {
  const sortedRows = [...rows].sort(
    (a, b) => dayjs(a.started_at).valueOf() - dayjs(b.started_at).valueOf()
  );

  const categories = sortedRows.map((row) => formatStartedAtLabel(row.started_at));
  const durations = sortedRows.map((row) => row.duration);
  const seriesCounts = sortedRows.map((row) => row.job_ids_count);

  const maxDuration = getNiceMax(Math.max(...durations, 0), 600);
  const maxSeriesCount = getNiceMax(Math.max(...seriesCounts, 0), 200);
  const durationInterval = maxDuration / 6;
  const seriesInterval = Math.max(1, Math.round(maxSeriesCount / 5));
  const showLabels = shouldShowSeriesLabels(categories.length);
  const hasManyPoints = categories.length > 15;

  return {
    grid: {
      left: 56,
      right: 56,
      top: 48,
      bottom: CHART_GRID_BOTTOM,
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const item = params as {
          seriesName: string;
          value: number;
          color: string;
          name: string;
          dataIndex: number;
        };

        const value =
          item.seriesName === 'Duração Total'
            ? formatDurationHms(item.value)
            : formatPtBrInteger(item.value);

        const categoryLabel = categories[item.dataIndex] ?? item.name;

        return `<div style="padding:6px 8px;">
          <div style="color:#575C66;font-size:12px;margin-bottom:4px;">${categoryLabel}</div>
          <div style="color:${item.color};font-size:13px;">
            ${item.seriesName}: <strong style="color:#1C2633;">${value}</strong>
          </div>
        </div>`;
      },
    },
    dataZoom: buildXAxisDataZoom(categories.length),
    legend: {
      show: true,
      left: 0,
      bottom: 0,
      itemGap: 20,
      data: ['Duração Total', 'Qtd. Séries'],
      textStyle: {
        color: '#1C2633',
        fontSize: 12,
      },
    },
    xAxis: {
      type: 'category',
      data: categories,
      axisLine: { show: true, lineStyle: { color: '#C6C8CC' } },
      axisTick: { alignWithLabel: true },
      axisLabel: {
        rotate: 45,
        color: '#575C66',
        fontSize: 9,
        margin: 12,
        hideOverlap: true,
        interval: hasManyPoints ? 'auto' : 0,
      },
      splitLine: { show: false },
    },
    yAxis: [
      {
        type: 'value',
        name: 'Duração',
        min: 0,
        max: maxDuration,
        interval: durationInterval,
        nameTextStyle: { color: '#575C66', fontSize: 12, padding: [0, 0, 0, 0] },
        axisLine: { show: true, lineStyle: { color: '#C6C8CC' } },
        axisLabel: {
          color: '#575C66',
          formatter: (value: number) => formatDurationHm(value),
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#E0E2E5' },
        },
      },
      {
        type: 'value',
        name: 'Qtd. Séries',
        min: 0,
        max: maxSeriesCount,
        interval: seriesInterval,
        position: 'right',
        nameTextStyle: { color: '#575C66', fontSize: 12 },
        axisLine: { show: true, lineStyle: { color: '#C6C8CC' } },
        axisLabel: {
          color: '#575C66',
          formatter: (value: number) => formatPtBrInteger(value),
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: 'Duração Total',
        type: 'line',
        yAxisIndex: 0,
        smooth: false,
        symbol: 'circle',
        symbolSize: hasManyPoints ? 5 : 8,
        color: DURATION_COLOR,
        lineStyle: { width: 2, color: DURATION_COLOR },
        itemStyle: { color: DURATION_COLOR, borderColor: DURATION_COLOR, borderWidth: 2 },
        label: {
          show: showLabels,
          position: 'top',
          distance: 8,
          color: DURATION_COLOR,
          fontSize: 11,
          formatter: (params) => formatDurationHms(params.value as number),
        },
        data: durations,
      },
      {
        name: 'Qtd. Séries',
        type: 'bar',
        yAxisIndex: 1,
        barMaxWidth: hasManyPoints ? 24 : 48,
        itemStyle: { color: SERIES_COLOR },
        label: {
          show: showLabels,
          position: 'insideTop',
          distance: 6,
          color: '#8B5A2B',
          fontSize: 11,
          formatter: (params) => formatPtBrInteger(params.value as number),
        },
        data: seriesCounts,
      },
    ],
  };
}
